"use client";

import { useEffect, useState } from "react";
import { X } from "lucide-react";

import {
  addAddress,
  updateAddress,
  type AddAddressRequest,
  type Address,
  type UpdateAddressRequest,
} from "@/lib/api/business-api";
import { usePageTranslation } from "@/hooks/usePageTranslation";
import { API_CONFIG } from "@/lib/config/api";

interface AddressFormProps {
  isOpen: boolean;
  onClose: () => void;
  onSuccess: (address: Address) => void;
  address?: Address | null;
  accessToken: string;
  businessId?: string;
}

const inputClass =
  "w-full rounded-brand border border-brand-border px-3 py-2 text-brand-fg focus:border-brand-primary focus:ring-2 focus:ring-brand-ring focus:outline-none";

/**
 * Country values are stored in English (backend contract); labels are the
 * translated `account.country*` keys.
 */
const COUNTRY_OPTIONS: Array<{ value: string; labelKey: string }> = [
  { value: "United Kingdom", labelKey: "account.countryUnitedKingdom" },
  { value: "United States", labelKey: "account.countryUnitedStates" },
  { value: "Canada", labelKey: "account.countryCanada" },
  { value: "Australia", labelKey: "account.countryAustralia" },
  { value: "Ireland", labelKey: "account.countryIreland" },
  { value: "Saudi Arabia", labelKey: "account.countrySaudiArabia" },
  { value: "Bangladesh", labelKey: "account.countryBangladesh" },
];

export function AddressForm({
  isOpen,
  onClose,
  onSuccess,
  address,
  accessToken,
  businessId = API_CONFIG.DEFAULT_BUSINESS_ID,
}: AddressFormProps) {
  const { t } = usePageTranslation();
  const isEditMode = !!address;

  // Form state
  const [formData, setFormData] = useState({
    label: "",
    is_default: true,
    first_name: "",
    last_name: "",
    phone: "",
    email: "",
    address_line_1: "",
    address_line_2: "",
    city: "",
    county: "",
    postcode: "",
    country: "United Kingdom",
  });

  const [isSubmitting, setIsSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // Initialize form with address data in edit mode
  useEffect(() => {
    if (address) {
      setFormData({
        label: address.label || "",
        is_default: address.is_default || false,
        first_name: address.first_name || "",
        last_name: address.last_name || "",
        phone: address.phone || "",
        email: address.email || "",
        address_line_1: address.address_line_1 || "",
        address_line_2: address.address_line_2 || "",
        city: address.city || "",
        county: address.county || "",
        postcode: address.postcode || "",
        country: address.country || "United Kingdom",
      });
    } else {
      // Reset form for add mode
      setFormData({
        label: "",
        is_default: true,
        first_name: "",
        last_name: "",
        phone: "",
        email: "",
        address_line_1: "",
        address_line_2: "",
        city: "",
        county: "",
        postcode: "",
        country: "United Kingdom",
      });
    }
    setError(null);
  }, [address, isOpen]);

  const handleChange = (
    e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement>
  ) => {
    const { name, value, type } = e.target;
    const checked = (e.target as HTMLInputElement).checked;

    setFormData((prev) => ({
      ...prev,
      [name]: type === "checkbox" ? checked : value,
    }));
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError(null);
    setIsSubmitting(true);

    try {
      if (isEditMode && address) {
        // Update existing address
        const updateData: UpdateAddressRequest = {
          address_id: address.id,
          businessId,
          ...formData,
        };

        const response = await updateAddress(accessToken, updateData);

        if (response.status && response.data) {
          onSuccess(response.data);
          onClose();
        } else {
          setError(response.message || t("account.updateAddressFailed"));
        }
      } else {
        // Add new address
        const addData: AddAddressRequest = {
          businessId,
          ...formData,
        };

        const response = await addAddress(accessToken, addData);

        console.log("Add address response:", response);

        if (response.status && response.data) {
          console.log(
            "Address added successfully, calling onSuccess and onClose"
          );
          onSuccess(response.data);
          onClose();
        } else {
          setError(response.message || t("account.addAddressFailed"));
        }
      }
    } catch (err) {
      console.error("Address form submission error:", err);
      setError(err instanceof Error ? err.message : t("auth.genericError"));
    } finally {
      setIsSubmitting(false);
    }
  };

  if (!isOpen) return null;

  const requiredMark = <span className="text-brand-danger">*</span>;
  const labelClass = "mb-1 block text-sm font-medium text-brand-fg";

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/30 p-4 backdrop-blur-sm">
      <div className="max-h-[90vh] w-full max-w-2xl overflow-y-auto rounded-brand-lg bg-brand-surface shadow-xl">
        {/* Header */}
        <div className="sticky top-0 flex items-center justify-between border-b border-brand-border bg-brand-surface px-6 py-4">
          <h2
            className="text-xl font-semibold text-brand-fg"
            style={{ fontFamily: "var(--brand-heading-font, inherit)" }}
          >
            {isEditMode ? t("account.editAddress") : t("checkout.addNewAddress")}
          </h2>
          <button
            onClick={onClose}
            className="text-brand-muted transition-colors hover:text-brand-fg"
            disabled={isSubmitting}
          >
            <X className="h-5 w-5" />
          </button>
        </div>

        {/* Form */}
        <form onSubmit={handleSubmit} className="space-y-4 px-6 py-4">
          {/* Error message */}
          {error && (
            <div className="rounded-brand border border-brand-danger/30 bg-brand-danger/10 px-4 py-3 text-brand-danger">
              {error}
            </div>
          )}

          {/* Address Label */}
          <div>
            <label htmlFor="label" className={labelClass}>
              {t("checkout.addressLabel")} {requiredMark}
            </label>
            <input
              type="text"
              id="label"
              name="label"
              value={formData.label}
              onChange={handleChange}
              required
              placeholder={t("account.addressLabelPlaceholder")}
              className={inputClass}
            />
          </div>

          {/* Name Fields */}
          <div className="grid grid-cols-1 gap-4 md:grid-cols-2">
            <div>
              <label htmlFor="first_name" className={labelClass}>
                {t("checkout.fieldFirstName")} {requiredMark}
              </label>
              <input
                type="text"
                id="first_name"
                name="first_name"
                value={formData.first_name}
                onChange={handleChange}
                required
                className={inputClass}
              />
            </div>

            <div>
              <label htmlFor="last_name" className={labelClass}>
                {t("checkout.fieldLastName")}
              </label>
              <input
                type="text"
                id="last_name"
                name="last_name"
                value={formData.last_name}
                onChange={handleChange}
                className={inputClass}
              />
            </div>
          </div>

          {/* Contact Fields */}
          <div className="grid grid-cols-1 gap-4 md:grid-cols-2">
            <div>
              <label htmlFor="phone" className={labelClass}>
                {t("checkout.fieldPhone")} {requiredMark}
              </label>
              <input
                type="tel"
                id="phone"
                name="phone"
                dir="ltr"
                value={formData.phone}
                onChange={handleChange}
                required
                className={inputClass}
              />
            </div>

            <div>
              <label htmlFor="email" className={labelClass}>
                {t("account.email")} {requiredMark}
              </label>
              <input
                type="email"
                id="email"
                name="email"
                dir="ltr"
                value={formData.email}
                onChange={handleChange}
                required
                className={inputClass}
              />
            </div>
          </div>

          {/* Address Lines */}
          <div>
            <label htmlFor="address_line_1" className={labelClass}>
              {t("checkout.addressLine1Label")} {requiredMark}
            </label>
            <input
              type="text"
              id="address_line_1"
              name="address_line_1"
              value={formData.address_line_1}
              onChange={handleChange}
              required
              placeholder={t("account.streetAddressPlaceholder")}
              className={inputClass}
            />
          </div>

          <div>
            <label htmlFor="address_line_2" className={labelClass}>
              {t("checkout.fieldAddressLine2")}
            </label>
            <input
              type="text"
              id="address_line_2"
              name="address_line_2"
              value={formData.address_line_2}
              onChange={handleChange}
              placeholder={t("account.addressLine2Placeholder")}
              className={inputClass}
            />
          </div>

          {/* City, County, Postcode */}
          <div className="grid grid-cols-1 gap-4 md:grid-cols-3">
            <div>
              <label htmlFor="city" className={labelClass}>
                {t("checkout.fieldCity")} {requiredMark}
              </label>
              <input
                type="text"
                id="city"
                name="city"
                value={formData.city}
                onChange={handleChange}
                required
                className={inputClass}
              />
            </div>

            <div>
              <label htmlFor="county" className={labelClass}>
                {t("account.county")}
              </label>
              <input
                type="text"
                id="county"
                name="county"
                value={formData.county}
                onChange={handleChange}
                className={inputClass}
              />
            </div>

            <div>
              <label htmlFor="postcode" className={labelClass}>
                {t("checkout.fieldPostcode")} {requiredMark}
              </label>
              <input
                type="text"
                id="postcode"
                name="postcode"
                dir="ltr"
                value={formData.postcode}
                onChange={handleChange}
                required
                className={inputClass}
              />
            </div>
          </div>

          {/* Country */}
          <div>
            <label htmlFor="country" className={labelClass}>
              {t("checkout.fieldCountry")} {requiredMark}
            </label>
            <select
              id="country"
              name="country"
              value={formData.country}
              onChange={handleChange}
              required
              className={inputClass}
            >
              {COUNTRY_OPTIONS.map((country) => (
                <option key={country.value} value={country.value}>
                  {t(country.labelKey)}
                </option>
              ))}
            </select>
          </div>

          {/* Action Buttons */}
          <div className="flex gap-3 border-t border-brand-border pt-4">
            <button
              type="button"
              onClick={onClose}
              disabled={isSubmitting}
              className="flex-1 rounded-brand border border-brand-border px-4 py-2 text-brand-fg transition-colors hover:bg-brand-fg/5 disabled:cursor-not-allowed disabled:opacity-50"
            >
              {t("common.cancel")}
            </button>
            <button
              type="submit"
              disabled={isSubmitting}
              className="flex-1 rounded-brand bg-brand-primary px-4 py-2 text-brand-primary-foreground transition-colors hover:bg-brand-primary-hover disabled:cursor-not-allowed disabled:opacity-50"
            >
              {isSubmitting
                ? isEditMode
                  ? t("account.updating")
                  : t("account.adding")
                : isEditMode
                  ? t("account.updateAddress")
                  : t("account.addAddress")}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}
